/**
 * Admin-Settings-Page — /admin/settings
 *
 * Zugriff: nur Admin (siehe RequireAdmin-Guard im App-Routing).
 *
 * Funktionen:
 *  1. Liste aller Feature-Flags mit Toggle (z.B. llm_suggestions).
 *  2. Bei llm_suggestions zusätzlich Doppel-Gate-Status (Mistral-API-Key
 *     im Deployment vorhanden?), Experimental-Warnbanner, und
 *  3. Wenn LLM aktiv: Usage-Stats für Cost-/Volumen-Monitoring.
 *
 * Audit-Hinweis am Ende: jeder Toggle-Klick landet im audit_log
 * (action='UPDATE', table='feature_flags').
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';

import {
  listAllFeatureFlags,
  setFeatureFlag,
  type FeatureFlag,
} from '../../lib/supabaseFeatureFlags';
import { getActiveProviderLabel, isLlmConfigured } from '../../lib/llm/llmClient';
import { useToast } from '../../components/ui/ToastProvider';
import { listLlmUsageStats, type LlmUsageStats } from '../../lib/supabaseLlmUsage';
import type { LlmCallStatus } from '../../lib/supabaseTypes';

const LLM_FLAG_KEY = 'llm_suggestions';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; flags: ReadonlyArray<FeatureFlag> }
  | { kind: 'error'; message: string };

export function Settings() {
  const toast = useToast();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [stats, setStats] = useState<LlmUsageStats | null>(null);
  const [statsLoading, setStatsLoading] = useState<boolean>(false);

  const llmReady = isLlmConfigured();
  const providerLabel = getActiveProviderLabel();

  const reload = useCallback(async (): Promise<void> => {
    try {
      const flags = await listAllFeatureFlags();
      setState({ kind: 'ready', flags });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Flags konnten nicht geladen werden.',
      });
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Stats nachladen, wenn LLM-Flag aktiv ist
  useEffect(() => {
    if (state.kind !== 'ready') {
      setStats(null);
      return;
    }
    const llmEnabled = state.flags.some((f) => f.key === LLM_FLAG_KEY && f.enabled);
    if (!llmEnabled) {
      setStats(null);
      return;
    }
    setStatsLoading(true);
    void listLlmUsageStats()
      .then((s) => {
        setStats(s);
      })
      .catch((err: unknown) => {
        console.warn('Settings: listLlmUsageStats failed', err);
        setStats(null);
      })
      .finally(() => {
        setStatsLoading(false);
      });
  }, [state]);

  async function handleToggle(flag: FeatureFlag): Promise<void> {
    setPendingKey(flag.key);
    try {
      await setFeatureFlag(flag.key, !flag.enabled);
      toast.show(
        flag.enabled
          ? `Feature „${flag.key}" wurde deaktiviert.`
          : `Feature „${flag.key}" wurde aktiviert.`,
        'success',
      );
      await reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Toggle fehlgeschlagen.';
      toast.show(msg, 'error');
    } finally {
      setPendingKey(null);
    }
  }

  return (
    <div className="mx-auto max-w-3xl pb-24 pt-12 md:pt-20">
      <header className="relative pb-10">
        <div
          aria-hidden="true"
          className="absolute right-0 top-0 hidden h-24 w-24 rounded-sm md:block"
          style={{ backgroundColor: 'var(--color-dim-resources)', opacity: 0.18 }}
        />
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          Administration
        </p>
        <h1 className="mt-3 font-serif text-4xl font-black leading-none tracking-tight text-stone-900 md:text-6xl">
          SYSTEM-EINSTELLUNGEN.
        </h1>
        <p className="mt-3 font-serif text-base italic text-stone-500 md:text-lg">
          Feature-Flags, Verfügbarkeit experimenteller Funktionen und Nutzungsstatistik. Jede
          Änderung wird im Audit-Log protokolliert.
        </p>
      </header>

      <section className="mb-10 rounded-sm border border-stone-200 bg-white">
        <div className="border-b border-stone-200 px-6 py-5 md:px-10">
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            Funktionen
          </p>
          <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900">
            FEATURE-FLAGS.
          </h2>
          <p className="mt-2 max-w-prose font-serif text-sm italic text-stone-500">
            Steuert Funktionen, die nicht für alle Berater dauerhaft aktiviert sind. Änderungen
            wirken mit max. 30 Sekunden Latenz (Cache-TTL) auf alle laufenden Sessions.
          </p>
        </div>

        <div className="divide-y divide-stone-200">
          {state.kind === 'loading' && (
            <p className="px-6 py-6 font-serif text-sm italic text-stone-500 md:px-10">
              Lädt Feature-Flags …
            </p>
          )}
          {state.kind === 'error' && (
            <p className="px-6 py-6 font-serif text-sm text-red-800 md:px-10">{state.message}</p>
          )}
          {state.kind === 'ready' &&
            state.flags.map((flag) => (
              <FeatureFlagCard
                key={flag.key}
                flag={flag}
                isPending={pendingKey === flag.key}
                llmReady={llmReady}
                providerLabel={providerLabel}
                onToggle={() => {
                  void handleToggle(flag);
                }}
              />
            ))}
        </div>
      </section>

      {/* Usage-Stats: nur sichtbar wenn LLM aktiv */}
      {state.kind === 'ready' && state.flags.some((f) => f.key === LLM_FLAG_KEY && f.enabled) && (
        <section className="mb-10 rounded-sm border border-stone-200 bg-white">
          <div className="border-b border-stone-200 px-6 py-5 md:px-10">
            <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
              LLM · Nutzungsstatistik
            </p>
            <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900">
              MISTRAL-CALLS.
            </h2>
            <p className="mt-2 max-w-prose font-serif text-sm italic text-stone-500">
              Stammt aus der append-only-Tabelle llm_usage_log. Ohne PII — nur SHA-256-Hash der
              pseudonymisierten Inputs, Token-Counts, Status. Aufbewahrung 10 Jahre (analog
              statement_versions).
            </p>
          </div>
          <div className="px-6 py-6 md:px-10">
            {statsLoading && (
              <p className="font-serif text-sm italic text-stone-500">Lädt Statistik …</p>
            )}
            {!statsLoading && stats && <UsageStatsBlock stats={stats} />}
            {!statsLoading && !stats && (
              <p className="font-serif text-sm italic text-stone-500">
                Keine Statistik verfügbar — DB-Zugriff fehlgeschlagen oder noch keine Calls.
              </p>
            )}
          </div>
        </section>
      )}

      <section className="rounded-sm border border-stone-200 bg-stone-50 px-6 py-5 md:px-10">
        <div className="flex items-start gap-3">
          <ShieldCheck
            size={18}
            className="mt-0.5 flex-shrink-0 text-stone-600"
            aria-hidden="true"
          />
          <div>
            <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700">
              Audit-Log
            </p>
            <p className="mt-1 max-w-prose font-serif text-sm leading-relaxed text-stone-700">
              Jede Aktivierung oder Deaktivierung eines Feature-Flags wird im audit_log
              protokolliert (table=feature_flags, action=UPDATE, mit User-ID und Zeitstempel). Bei
              einer DSB-Prüfung ist nachvollziehbar, wer wann welches Feature geschaltet hat.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FeatureFlagCard
// ---------------------------------------------------------------------------

interface FeatureFlagCardProps {
  readonly flag: FeatureFlag;
  readonly isPending: boolean;
  readonly llmReady: boolean;
  readonly providerLabel: string;
  readonly onToggle: () => void;
}

function FeatureFlagCard({
  flag,
  isPending,
  llmReady,
  providerLabel,
  onToggle,
}: FeatureFlagCardProps) {
  const isLlmFlag = flag.key === LLM_FLAG_KEY;
  // Toggle nur disabled, wenn das LLM-Flag aktiviert werden soll, der
  // API-Key aber nicht da ist. Disable kommt zusätzlich, wenn die DB-Schreibung
  // gerade läuft.
  const disableTrigger = isPending || (isLlmFlag && !llmReady && !flag.enabled);
  const cardLabel = humanizeFlagKey(flag.key);

  return (
    <article className="px-6 py-6 md:px-10">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3 className="font-serif text-lg font-bold text-stone-900">{cardLabel}</h3>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-stone-500">
            {flag.key}
          </p>
          <p className="mt-3 max-w-prose font-serif text-sm leading-relaxed text-stone-700">
            {flag.description}
          </p>
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={flag.enabled}
          aria-label={`Feature ${cardLabel} ${flag.enabled ? 'deaktivieren' : 'aktivieren'}`}
          disabled={disableTrigger}
          onClick={onToggle}
          className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full border transition-colors ${
            flag.enabled
              ? 'border-stone-900 bg-stone-900'
              : 'border-stone-300 bg-stone-200 hover:border-stone-500'
          } disabled:cursor-not-allowed disabled:opacity-50`}
        >
          <span
            aria-hidden="true"
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              flag.enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      <p className="mt-3 font-sans text-[10px] uppercase tracking-widest text-stone-500">
        Zuletzt geändert {formatChangedBy(flag)}
      </p>

      {/* Spezial-Warnung beim LLM-Flag: kein LLM-Provider konfiguriert */}
      {isLlmFlag && !llmReady && (
        <div className="mt-4 flex items-start gap-3 rounded-sm border border-amber-300 bg-amber-50 px-4 py-3">
          <AlertTriangle
            size={16}
            className="mt-0.5 flex-shrink-0 text-amber-700"
            aria-hidden="true"
          />
          <div>
            <p className="font-sans text-xs font-medium uppercase tracking-widest text-amber-800">
              Kein LLM-Provider konfiguriert
            </p>
            <p className="mt-1 font-serif text-sm text-amber-900">
              In der Umgebungskonfiguration ist weder <code>VITE_ANTHROPIC_API_KEY</code> noch{' '}
              <code>VITE_MISTRAL_API_KEY</code> gesetzt. Bitte einen der beiden als Encrypted
              Environment Variable in den Cloudflare-Pages-Settings hinterlegen und das Projekt neu
              deployen — erst dann ist der Toggle freigeschaltet.
            </p>
          </div>
        </div>
      )}

      {/* Experimental-Warnung beim LLM-Flag, wenn ein Provider konfiguriert ist */}
      {isLlmFlag && llmReady && (
        <div className="mt-4 flex items-start gap-3 rounded-sm border border-red-300 bg-red-50 px-4 py-3">
          <AlertTriangle
            size={16}
            className="mt-0.5 flex-shrink-0 text-red-700"
            aria-hidden="true"
          />
          <div>
            <p className="font-sans text-xs font-medium uppercase tracking-widest text-red-800">
              Experimental — vor Aktivierung lesen
            </p>
            <p className="mt-1 font-serif text-sm leading-relaxed text-red-900">
              Aktiver Provider: <strong className="font-mono text-red-950">{providerLabel}</strong>.
              Aktivierung darf erst nach Abschluss der DSFA, dem DSB-Workshop und dem AVV mit dem
              Anbieter erfolgen. Aktuell zulässig nur für interne Tests mit synthetischen oder
              Test-Mandanten — keine echten Mandantendaten.
            </p>
          </div>
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// UsageStatsBlock
// ---------------------------------------------------------------------------

const STATUS_LABEL: Readonly<Record<LlmCallStatus, string>> = {
  success: 'Erfolgreich',
  error: 'Fehler',
  blocked_flag: 'Geblockt (Flag deaktiviert)',
  blocked_pseudonymization: 'Geblockt (PII-Pattern)',
};

interface UsageStatsBlockProps {
  readonly stats: LlmUsageStats;
}

function UsageStatsBlock({ stats }: UsageStatsBlockProps) {
  const statusEntries = Object.entries(stats.byStatus) as ReadonlyArray<[LlmCallStatus, number]>;
  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatCard label="Gesamt-Calls" value={String(stats.total)} />
        <StatCard label="Letzte 24 Stunden" value={String(stats.last24h)} />
        <StatCard
          label="Erfolgsrate"
          value={
            stats.total > 0
              ? `${String(Math.round((stats.byStatus.success / stats.total) * 100))} %`
              : '—'
          }
        />
      </div>

      <div className="mt-6">
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          Verteilung nach Status
        </p>
        <ul className="mt-2 divide-y divide-stone-200 border-t border-stone-200">
          {statusEntries.map(([status, count]) => (
            <li
              key={status}
              className="flex items-center justify-between px-1 py-3 font-serif text-sm"
            >
              <span className="text-stone-700">{STATUS_LABEL[status]}</span>
              <span className="font-mono text-stone-900">{String(count)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function StatCard({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="rounded-sm border border-stone-200 bg-stone-50 px-4 py-3">
      <p className="font-sans text-[10px] font-medium uppercase tracking-widest text-stone-500">
        {label}
      </p>
      <p className="mt-1 font-serif text-2xl font-bold text-stone-900">{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers — exportiert für Tests
// ---------------------------------------------------------------------------

/**
 * Macht aus 'llm_suggestions' → 'LLM-Bewertungsvorschläge'.
 * Bewusst hart-kodierte Liste statt automatischer Heuristik; lieber bei
 * jedem neuen Flag den Label hier ergänzen als generische Snake-Case-
 * Übersetzung.
 */
export function humanizeFlagKey(key: string): string {
  switch (key) {
    case 'llm_suggestions':
      return 'LLM-Bewertungsvorschläge';
    default:
      return key
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
  }
}

/**
 * Formatiert die „Zuletzt geändert von X am Y"-Zeile aus dem Flag-Row.
 * Wenn updated_by null ist (system-seeded), wird das benannt.
 */
export function formatChangedBy(flag: FeatureFlag): string {
  const when = flag.updatedAt.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  if (!flag.updatedBy) {
    return `am ${when} (System-Seed)`;
  }
  return `von ${flag.updatedBy} am ${when}`;
}
